import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { map } from 'rxjs';
import { EnvironmentLoaderService } from './environment-loader.service';
import { PublicLocationV3, PublicLocationsV3Root } from './public-locations-v2.dto';
import { GeoZonesRoot, Location, LocationDataRoot, RestrictedLocation } from 'src/app/features/ok-app-emulator/ok-app-emulator.model';
import { LatLngBounds } from '../models/bounds.model';
import { Guid } from 'guid-typescript';
import { ChargingUserAccessGroup, InvitationDetails } from 'src/app/features/charging-users/charging-user.model';

@Injectable({
  providedIn: 'root',
})
export class AppDataService {
  private environmentLoaderService = inject(EnvironmentLoaderService);
  private httpClient = inject(HttpClient);

  apiUrlAppDataV1: string;
  apiUrlV1: string;
  apiUrlV3: string;
  apiUrlAppDataV3: string;
  apiUrlV4: string;

  constructor() {
    this.apiUrlAppDataV1 = `${this.environmentLoaderService.config['serviceBaseUrl']}/api/v1/appData`;
    this.apiUrlV1 = `${this.environmentLoaderService.config['serviceBaseUrl']}/api/v1`;
    this.apiUrlV3 = `${this.environmentLoaderService.config['serviceBaseUrl']}/api/v3`;
    this.apiUrlAppDataV3 = `${this.environmentLoaderService.config['serviceBaseUrl']}/api/v3/appData`;
    this.apiUrlV4 = `${this.environmentLoaderService.config['serviceBaseUrl']}/api/v4`;
  }

  public getPublicLocationsApiRequestUrl(): string {
    return `${this.apiUrlV3}/locations/public`;
  }
  public getPublicLocations(): Observable<GeoZonesRoot> {
    return this.httpClient
      .get<PublicLocationsV3Root>(this.getPublicLocationsApiRequestUrl())
      .pipe(map(response => this.mapPublicLocationsV3ToGeoZonesRoot(response)));
  }

  // Maps the snake_case v3 payload to the camelCase model used across the app.
  private mapPublicLocationsV3ToGeoZonesRoot(response: PublicLocationsV3Root): GeoZonesRoot {
    return {
      geoZones: response.geo_zones.map(geoZone => ({
        geoZoneId: geoZone.geo_zone_id,
        locations: geoZone.locations.map(location => this.mapPublicLocationV3ToLocation(location)),
      })),
    };
  }

  private mapPublicLocationV3ToLocation(location: PublicLocationV3): Location {
    return {
      locationId: location.location_id,
      latitude: location.latitude,
      longitude: location.longitude,
      name: location.name,
      geoZoneId: location.geo_zone_id,
      netAreaCode: location.net_area_code,
      visibilityComment: location.visibility_comment ?? '',
      maxChargingPowerOnLocation: location.max_charging_power_on_location,
      locationAppVisibility: location.location_app_visibility,
      address: {
        city: location.address.city ?? '',
        postalCode: location.address.postal_code ?? '',
        road: location.address.road ?? '',
        number: location.address.number ?? 0,
        letter: location.address.letter ?? '',
        konceptAnlaeg: location.address.concept_installation_number,
      },
      chargingStations: location.charging_stations.map(chargingStation => ({
        chargingStationId: chargingStation.charging_station_id,
        connectors: chargingStation.connectors.map(connector => ({
          connectorId: connector.connector_id,
          stationNumber: connector.station_number ?? 0,
          globalEvseId: connector.evse_id,
          power: connector.power,
        })),
      })),
    } as Location;
  }

  public getRestrictedLocationsApiRequestUrl(): string {
    return `${this.apiUrlAppDataV1}/Locations/Restricted`;
  }

  public getRestrictedLocations(
    bounds: LatLngBounds,
    customerNumbers: number[],
    mokUserNumber: number,
    deviceId: string | null
  ): Observable<RestrictedLocation[]> {
    const body: Record<string, unknown> = {
      bounds: bounds,
      mokBrugernr: mokUserNumber,
      kundenr: customerNumbers,
    };
    if (deviceId) {
      body['deviceId'] = deviceId;
    }
    return this.httpClient.post<RestrictedLocation[]>(this.getRestrictedLocationsApiRequestUrl(), body);
  }

  public getLocationDetailsApiRequestUrlV1(locationId: string): string {
    const detailsUrl = `${this.apiUrlAppDataV1}/location/${locationId}/details/`;
    return detailsUrl;
  }

  public getLocationDetailsApiRequestUrlV3(
    locationId: string,
    mokUserNumber: number | null,
    customerNumbers: number[] | null,
    deviceId: string | null
  ): string {
    if (!deviceId) {
      deviceId = Guid.createEmpty().toString();
    }
    let detailsUrl = `${this.apiUrlAppDataV3}/location/${locationId}/details/` + deviceId;
    let params = new HttpParams();

    if (mokUserNumber) {
      params = params.set('mokUserNumber', mokUserNumber.toString());
    }

    if (customerNumbers && customerNumbers.length > 0) {
      params = params.set('customerNumbers', customerNumbers.join(','));
    }

    if (params.toString().length > 0) {
      detailsUrl = detailsUrl + '?' + params.toString();
    }
    return detailsUrl;
  }

  public getLocationDetailsApiRequestUrlV4(
    locationId: string,
    mokUserNumber: number | null,
    customerNumbers: number[] | null,
    deviceId: string | null
  ): string {
    if (!deviceId) {
      deviceId = Guid.createEmpty().toString();
    }
    let params = new HttpParams().set('device-id', deviceId);

    if (mokUserNumber) {
      params = params.set('mok-user-number', mokUserNumber.toString());
    }

    if (customerNumbers && customerNumbers.length > 0) {
      params = params.set('customer-numbers', customerNumbers.join(','));
    }

    return `${this.apiUrlV4}/locations/${locationId}/details?${params.toString()}`;
  }

  // v4 omits the connector-level chargingStationId that v1/v3 redundantly include; backfill it from the parent charging station
  private mapLocationDetailsV4Response(response: LocationDataRoot): LocationDataRoot {
    response.locationData.chargingStations.forEach(chargingStation => {
      chargingStation.connectors.forEach(connector => {
        connector.chargingStationId = chargingStation.chargingStationId;
      });
    });
    return response;
  }

  public getLocationDetails(
    locationId: string,
    mokUserNumber: number | null,
    customerNumbers: number[] | null,
    version: string,
    deviceId: string | null
  ): Observable<LocationDataRoot> {
    if (!deviceId) {
      deviceId = Guid.createEmpty().toString();
    }

    if (version === 'v4') {
      const detailsUrl = this.getLocationDetailsApiRequestUrlV4(locationId, mokUserNumber, customerNumbers, deviceId);
      return this.httpClient.get<LocationDataRoot>(detailsUrl).pipe(map(response => this.mapLocationDetailsV4Response(response)));
    }

    let detailsUrl: string = this.getLocationDetailsApiRequestUrlV1(locationId);

    if (version === 'v3') {
      detailsUrl = this.getLocationDetailsApiRequestUrlV3(locationId, mokUserNumber, customerNumbers, deviceId);
    }

    return this.httpClient.get<LocationDataRoot>(detailsUrl);
  }

  public getAccessGroups(mokUserNumber: number, deviceId: string): Observable<ChargingUserAccessGroup> {
    const chargingUsersAccessGroupsUrl = `${this.apiUrlV1}/charging-users/access-groups`;
    let params = new HttpParams();

    if (mokUserNumber) {
      params = params.set('mok_user_number', mokUserNumber);
    }

    if (deviceId) {
      params = params.set('device_id', deviceId);
    } else {
      params = params.set('device_id', '00000000-0000-0000-0000-000000000001');
    }

    return this.httpClient.get<ChargingUserAccessGroup>(chargingUsersAccessGroupsUrl + '?' + params.toString());
  }

  public getInvitationDetails(invitationIdentifier: string): Observable<InvitationDetails> {
    const chargingUsersAccessGroupsUrl = `${this.apiUrlV1}/AppData/InvitationDetails/${invitationIdentifier}`;
    return this.httpClient.get<InvitationDetails>(chargingUsersAccessGroupsUrl);
  }
}
